import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import Accordion from "@/components/base/Accordion";
import SectionHeading from "@/components/base/SectionHeading";
import Eyebrow from "@/components/base/Eyebrow";
import MicroLabel from "@/components/base/MicroLabel";
import Tag from "@/components/base/Tag";
import OrderTypeCard from "@/components/feature/OrderTypeCard";
import CtaBand from "@/components/feature/CtaBand";
import Ticker from "@/components/feature/Ticker";
import PricePathChart from "@/components/feature/PricePathChart";
import BrokerPartners from "@/components/feature/BrokerPartners";
import OrderTakeaway from "@/pages/home/components/OrderTakeaway";
import OutcomeCompare from "@/pages/home/components/OutcomeCompare";
import OrderTicket from "@/pages/home/components/OrderTicket";
import { comparisonRows, extendedTypes, orderTypes } from "@/mocks/orderTypes";
import { faqs } from "@/mocks/siteData";

const concepts = [
  {
    title: "Bid & Ask",
    text: "You sell at the lower bid and buy at the higher ask. The gap between them is the spread you pay on every trade, both entering and exiting.",
  },
  {
    title: "Execution speed",
    text: "How fast an order is filled. Market orders fire instantly; limit orders wait for the market to come to them.",
  },
  {
    title: "Slippage",
    text: "The gap between the price you expected and the price you got — the hidden tax on market and stop orders.",
  },
];

const steps = [
  {
    title: "Certainty or price?",
    text: "Need to be in right now? Market order. Need a specific price? Limit order. This one question decides most of your choices.",
  },
  {
    title: "Guard every position",
    text: "Attach a stop-loss the moment you open a leveraged trade. It caps your intended loss before emotion takes the wheel.",
  },
  {
    title: "Stop or stop-limit?",
    text: "Value a guaranteed exit? Use a stop. Value a guaranteed price? Use a stop-limit and accept the fill risk.",
  },
  {
    title: "Match the pair",
    text: "On liquid majors you can be precise. On thin or exotic pairs, widen stops and expect more slippage.",
  },
];

const whyItems = [
  { text: "Stops turn risk into a fixed, known number the moment you're in the trade." },
  { text: "Limits set an exact entry price and let the market come to you." },
  { text: "The right order keeps your discipline intact when the screen gets loud." },
];

export default function Home() {
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const [tableAtEnd, setTableAtEnd] = useState(false);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };

  const handleTableScroll = () => {
    const el = tableScrollRef.current;
    if (!el) return;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    setTableAtEnd(atEnd);
  };

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-foreground-950">
        <div className="absolute inset-0 grid-pattern-light"></div>
        <span className="absolute -right-6 bottom-[-3rem] font-heading font-black uppercase leading-none text-primary-500/10 text-[16rem] md:text-[26rem] select-none pointer-events-none" aria-hidden="true">
          FX
        </span>

        <div className="relative px-4 md:px-8 pt-32 md:pt-40 pb-14">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 border-2 border-primary-500 px-3 py-1.5 font-label text-[11px] uppercase tracking-[0.2em] text-primary-500">
                The forex order type handbook
              </span>
              <h1 className="mt-7 font-heading font-black uppercase leading-[0.84] tracking-tight text-background-50 text-[2.75rem] sm:text-6xl lg:text-6xl xl:text-[6.5rem]">
                Every forex
                <br />
                order type,
                <br />
                <span className="bg-primary-500 text-foreground-950 px-2 inline-block">explained.</span>
              </h1>
              <p className="mt-7 text-base md:text-lg leading-relaxed text-background-300 max-w-xl">
                Market, limit, stop and stop-limit orders each solve a different problem. Learn how
                they execute, what they really cost, and exactly when to use each one.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Link
                  to="/market-orders"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 border-2 border-primary-500 bg-primary-500 text-foreground-950 font-label text-xs uppercase tracking-[0.16em] font-semibold whitespace-nowrap hover:bg-background-50 hover:border-background-50 transition-colors cursor-pointer"
                >
                  Start with market orders
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/order-execution"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 border-2 border-background-50/40 text-background-50 font-label text-xs uppercase tracking-[0.16em] font-semibold whitespace-nowrap hover:border-primary-500 hover:text-primary-500 transition-colors cursor-pointer"
                >
                  <i className="ri-play-circle-line text-base"></i>
                  How execution works
                </Link>
              </div>
            </div>

            {/* Order ticket */}
            <div className="lg:col-span-5">
              <OrderTicket />
            </div>
          </div>
        </div>
      </section>

      <Ticker />

      {/* What is an order type */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-background-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <Eyebrow>The basics</Eyebrow>
            <h2 className="mt-4 font-heading font-black uppercase leading-[0.92] tracking-tight text-foreground-950 text-3xl md:text-5xl">
              What is a forex order type?
            </h2>
            <p className="mt-6 text-sm md:text-base leading-relaxed text-foreground-600">
              An order type is the instruction you give your broker about{" "}
              <strong className="font-bold text-foreground-950">how</strong> and{" "}
              <strong className="font-bold text-foreground-950">when</strong> to trade. Filled
              instantly, or only at a price you name? Should it wait for a trigger level before
              acting?
            </p>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              It is one of the most practical skills in trading. It decides whether you control your
              price, whether you are guaranteed a fill, and how far the market can move against you
              before your plan takes over.
            </p>
          </Reveal>

          <div className="lg:col-span-7">
            <Eyebrow tone="muted" className="mb-5">
              Three concepts to know first
            </Eyebrow>
            <div className="border-t-2 border-foreground-950">
              {concepts.map((c, index) => (
                <Reveal key={c.title} delay={index * 80}>
                  <div className="flex items-start gap-5 py-6 border-b-2 border-foreground-950">
                    <span
                      className="w-10 shrink-0 pt-0.5 font-heading font-black text-xl leading-none tabular-nums text-foreground-950"
                      aria-hidden="true"
                    >
                      0{index + 1}
                    </span>
                    <div>
                      <h3 className="font-heading font-bold uppercase tracking-tight text-lg text-foreground-950">
                        {c.title}
                      </h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{c.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why it matters */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-background-100 border-y-2 border-foreground-950">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5 order-2 lg:order-1">
            <OutcomeCompare />
          </Reveal>
          <Reveal delay={120} className="lg:col-span-7 order-1 lg:order-2">
            <Eyebrow>Why it matters</Eyebrow>
            <h2 className="mt-4 font-heading font-black uppercase leading-[0.92] tracking-tight text-foreground-950 text-3xl md:text-5xl">
              The order type is where your plan meets the market
            </h2>
            <p className="mt-6 text-sm md:text-base leading-relaxed text-foreground-600">
              Two traders can share the same price view and still get wildly different results —
              purely because of the orders they used. A market order chases the price; a limit order
              names it; a stop order reacts to it.
            </p>
            <div className="mt-8 border-t-2 border-foreground-950">
              {whyItems.map((item) => (
                <div
                  key={item.text}
                  className="flex items-start gap-4 py-4 border-b-2 border-foreground-950"
                >
                  <span
                    className="mt-2 block w-1.5 h-1.5 shrink-0 bg-primary-600"
                    aria-hidden="true"
                  ></span>
                  <span className="text-sm md:text-base leading-relaxed text-foreground-700">
                    {item.text}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Order type cards */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <SectionHeading
              eyebrow="Core order types"
              title="Four building blocks of every trade"
            />
            <p className="max-w-md text-sm leading-relaxed text-foreground-600 lg:pb-2">
              Almost every order you place is a variation of these four — each one carries a worked
              example and the exact trade-offs spelled out.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {orderTypes.map((type, index) => (
              <Reveal key={type.slug} delay={index * 80}>
                <OrderTypeCard
                  slug={type.slug}
                  name={type.name}
                  category={type.category}
                  tagline={type.tagline}
                  summary={type.summary}
                  index={`0${index + 1}`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section id="compare" className="px-4 md:px-8 py-16 md:py-24 bg-background-100 border-t-2 border-foreground-950 scroll-mt-[68px]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div className="max-w-3xl">
              <Eyebrow>At a glance</Eyebrow>
              <h2 className="mt-4 font-heading font-black uppercase leading-[0.92] tracking-tight text-foreground-950 text-3xl md:text-5xl">
                Order types, compared
              </h2>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-foreground-600 lg:pb-2">
              Read each row as: what it does, how much control you keep, who it is best for, and
              what can go wrong.
            </p>
          </div>

          <div className="mt-12 relative">
            <div
              ref={tableScrollRef}
              onScroll={handleTableScroll}
              className="overflow-x-auto border-2 border-foreground-950"
            >
              <table className="w-full min-w-[760px] border-collapse text-left">
              <caption className="sr-only">
                Comparison of forex order types — market, limit, stop and stop-limit — by execution,
                price control, best use and main risk.
              </caption>
              <thead>
                <tr className="bg-primary-500 text-foreground-950">
                  <th scope="col" className="px-5 py-4 font-label text-[11px] uppercase tracking-[0.16em] border-r-2 border-foreground-950">Order type</th>
                  <th scope="col" className="px-5 py-4 font-label text-[11px] uppercase tracking-[0.16em] border-r-2 border-foreground-950">Execution</th>
                  <th scope="col" className="px-5 py-4 font-label text-[11px] uppercase tracking-[0.16em] border-r-2 border-foreground-950">Price control</th>
                  <th scope="col" className="px-5 py-4 font-label text-[11px] uppercase tracking-[0.16em] border-r-2 border-foreground-950">Best for</th>
                  <th scope="col" className="px-5 py-4 font-label text-[11px] uppercase tracking-[0.16em]">Main risk</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr
                    key={row.slug}
                    className="border-t-2 border-foreground-950 transition-colors hover:bg-background-200/40"
                  >
                    <td className="px-5 py-5 border-r-2 border-foreground-950/20">
                      <Link
                        to={`/${row.slug}`}
                        className="font-heading font-bold uppercase tracking-tight text-foreground-950 hover:text-accent-600 transition-colors cursor-pointer"
                      >
                        {row.name}
                      </Link>
                    </td>
                    <td className="px-5 py-5 text-sm text-foreground-700 border-r-2 border-foreground-950/20">{row.execution}</td>
                    <td className="px-5 py-5 text-sm text-foreground-700 border-r-2 border-foreground-950/20">{row.priceControl}</td>
                    <td className="px-5 py-5 text-sm text-foreground-700 border-r-2 border-foreground-950/20">{row.bestFor}</td>
                    <td className="px-5 py-5">
                      <Tag tone="accent">{row.risk}</Tag>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            <div
              aria-hidden="true"
              className={`pointer-events-none absolute top-[3px] bottom-[3px] right-[3px] w-12 bg-gradient-to-l from-background-100 to-transparent transition-opacity duration-200 md:hidden ${
                tableAtEnd ? "opacity-0" : "opacity-100"
              }`}
            ></div>

            <div
              className={`md:hidden mt-3 flex items-center gap-2 font-label text-[10px] uppercase tracking-[0.16em] text-foreground-500 transition-opacity duration-200 ${
                tableAtEnd ? "opacity-0" : "opacity-100"
              }`}
            >
              <i className="ri-drag-move-2-line text-sm"></i>
              Swipe to see all columns
            </div>
          </div>
        </div>
      </section>

      <BrokerPartners />

      <PricePathChart />

      <OrderTakeaway />

      {/* Extended types */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-background-100 border-y-2 border-foreground-950">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            eyebrow="Beyond the basics"
            title="Variations you will meet next"
            description="Once the four core types click, these are the tools built on top of them."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {extendedTypes.map((type, index) => (
              <Reveal key={type.name} delay={index * 80} className="h-full">
                <div className="h-full flex flex-col border-2 border-foreground-950 bg-background-50 p-6 transition-colors hover:border-accent-500">
                  <MicroLabel mark="dot" tone="muted">
                    Built on
                    <span className="font-semibold text-accent-700">{type.builtOn}</span>
                  </MicroLabel>
                  <h3 className="mt-4 font-heading font-bold uppercase tracking-tight text-lg text-foreground-950">
                    {type.name}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600 min-h-[7.25rem]">{type.text}</p>
                  <div className="mt-5 pt-4 border-t-2 border-foreground-950/15">
                    <MicroLabel tone="accent">Used for</MicroLabel>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600 min-h-[3.25em]">
                      {type.use}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How to choose */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            eyebrow="Decision guide"
            title="How to choose the right order type"
            description="Work through these four questions and the answer almost always falls out naturally."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((step, index) => (
              <Reveal key={step.title} delay={index * 80}>
                <div className="h-full border-2 border-foreground-950 bg-background-50 p-6">
                  <span aria-hidden="true" className="font-heading font-black text-5xl leading-none text-stroke text-foreground-950">
                    0{index + 1}
                  </span>
                  <h3 className="mt-5 font-heading font-bold uppercase tracking-tight text-lg text-foreground-950">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-background-100 border-y-2 border-foreground-950">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <SectionHeading
              eyebrow="FAQ"
              title="Common questions"
              description="Straight answers to the questions traders ask most before placing their first order."
            />
            <Link
              to="/glossary"
              className="mt-8 inline-flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.16em] font-semibold text-foreground-950 border-b-2 border-foreground-950 pb-1 hover:text-accent-600 hover:border-accent-600 transition-colors cursor-pointer"
            >
              Browse the full glossary
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="lg:col-span-8">
            <Accordion items={faqs} />
          </div>
        </div>
      </section>

      <CtaBand
        title="Ready to place your first order with confidence?"
        description="Start with the market order, then work through limit, stop and stop-limit. Each page gives you a worked example you can read in two minutes."
        to="/market-orders"
        buttonLabel="Open market orders"
      />
    </>
  );
}